"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.HeatmapCore = void 0;
var tslib_1 = require("tslib");
var class_names_1 = require("./class-names");
var d3 = tslib_1.__importStar(require("./d3-modules"));
var scales_1 = require("./scales");
var state_1 = require("./state");
var utils_1 = require("./utils");
/** This class provides very basic functionality for a heatmap,
 * i.e. state keeping, rendering to HTML, and a mechanism for adding extensions.
 * More functionality is added via extensions in the `Heatmap` class.
 * `HeatmapCore` is not really usable alone, but could be useful in custom builds. */
var HeatmapCore = /** @class */function () {
  function HeatmapCore(dataDescription) {
    var _this = this;
    this.state = new state_1.State(dataDescription);
    this.state.events.resize.subscribe(function (size) {
      if (!size) return;
      var canvasScale = _this.state.canvasScale;
      _this.state.boxes.svg = scales_1.Box.create(0, 0, size.width, size.height);
      _this.state.boxes.canvasContext = scales_1.Box.create(0, 0, canvasScale * size.width, canvasScale * size.height);
      _this.state.scales = (0, scales_1.Scales)(_this.state.boxes);
    });
    this.state.events.hover.subscribe(function (e) {
      var _a, _b;
      return (_a = _this.state.dom) === null || _a === void 0 ? void 0 : _a.svg.attr('pointing-data', ((_b = e.cell) === null || _b === void 0 ? void 0 : _b.datum) !== undefined ? '' : null);
    });
  }
  /** Update `canvasScale` based on screen resolution and browser zoom level. */
  HeatmapCore.prototype.updateCanvasScale = function () {
    var _a;
    var devicePixelRatio = (_a = window === null || window === void 0 ? void 0 : window.devicePixelRatio) !== null && _a !== void 0 ? _a : 1;
    if (devicePixelRatio === this.state.canvasScale) return;
    this.state.canvasScale = devicePixelRatio;
    this.state.events.resize.next(this.state.events.resize.value);
  };
  /** Register an extension with this heatmap, i.e. create a behavior bound to the state of this heatmap. */
  HeatmapCore.prototype.registerExtension = function (extension, params) {
    var behavior = extension.create(this.state, params);
    behavior.register();
    return behavior;
  };
  /** Render this heatmap in the given DIV element. */
  HeatmapCore.prototype.render = function (divElementOrId) {
    var _this = this;
    if (this.state.dom) {
      console.error("This ".concat(this.constructor.name, " has already been rendered in element"), this.state.dom.rootDiv.node());
      throw new Error("This ".concat(this.constructor.name, " has already been rendered. Cannot render again."));
    }
    // Rendering this DOM structure:
    // <div id="${divElementOrId}">                          // dom.rootDiv   - existing div provided by the caller
    //     <div class="heatmap-main-div"">                   // dom.mainDiv   - whole heatmap component (possibly including axis labels etc. in future)
    //         <div class="heatmap-canvas-div"">             // dom.canvasDiv - canvas parts of the component (ensures perfect alignment of <canvas> and <svg>)
    //             <canvas class="heatmap-canvas"></canvas>  // dom.canvas    - used to draw data efficiently, no interactivity
    //             <svg class="heatmap-svg"></svg>           // dom.svg       - used to add interactivity (event listeners) and render simple things, e.g. markers
    //         </div>
    //     </div>
    // </div>
    var rootDiv = typeof divElementOrId === 'string' ? d3.select("#".concat(divElementOrId)) : d3.select(divElementOrId);
    if (rootDiv.empty()) throw new Error('Failed to initialize, wrong div ID?');
    this.remove();
    var mainDiv = (0, utils_1.attrd)(rootDiv.append('div'), {
      class: class_names_1.Class.MainDiv,
      style: {
        position: 'relative',
        width: '100%',
        height: '100%'
      }
    });
    var canvasDiv = (0, utils_1.attrd)(mainDiv.append('div'), {
      class: class_names_1.Class.CanvasDiv,
      style: {
        position: 'absolute',
        width: '100%',
        height: '100%'
      }
    });
    var canvas = (0, utils_1.attrd)(canvasDiv.append('canvas'), {
      class: class_names_1.Class.Canvas,
      width: 100,
      // Initial canvas size doesn't matter because it will be immediately resized
      height: 100,
      // Initial canvas size doesn't matter because it will be immediately resized
      style: {
        position: 'absolute',
        width: '100%',
        height: '100%'
      }
    });
    var svg = (0, utils_1.attrd)(canvasDiv.append('svg'), {
      class: class_names_1.Class.Svg,
      style: {
        position: 'absolute',
        width: '100%',
        height: '100%'
      }
    });
    this.state.dom = {
      rootDiv: rootDiv,
      mainDiv: mainDiv,
      canvasDiv: canvasDiv,
      canvas: canvas,
      svg: svg
    };
    // Add event listeners
    svg.on('mousemove.heatmapcore', function (e) {
      return _this.state.events.hover.next({
        cell: _this.state.getPointedCell(e),
        sourceEvent: e
      });
    });
    svg.on('mouseleave.heatmapcore', function (e) {
      return _this.state.events.hover.next({
        cell: undefined,
        sourceEvent: e
      });
    });
    svg.on('click.heatmapcore', function (e) {
      return _this.state.events.select.next({
        cell: _this.state.getPointedCell(e),
        sourceEvent: e
      });
    });
    this.state.events.render.next(undefined);
    this.sizeObserver = new ResizeObserver(function () {
      return _this.state.emitResize();
    });
    for (var _i = 0, _a = canvas.nodes(); _i < _a.length; _i++) {
      var node = _a[_i];
      this.sizeObserver.observe(node);
    }
    this.updateCanvasScale();
    d3.select(window).on('resize.updateCanvasScale', function () {
      return _this.updateCanvasScale();
    });
    return this;
  };
  /** Clear all the contents of the root div. */
  HeatmapCore.prototype.remove = function () {
    var _a;
    (_a = this.sizeObserver) === null || _a === void 0 ? void 0 : _a.disconnect();
    this.sizeObserver = undefined;
    if (!this.state.dom) return;
    this.state.dom.rootDiv.select('*').remove();
  };
  return HeatmapCore;
}();
exports.HeatmapCore = HeatmapCore;